import { NEUTRAL, SERIES } from "./chartColors";
import { formatCount as fmt, formatShare } from "./format";

const UNSPECIFIED = "UNSPECIFIED";

// Study areas are typed inconsistently ("NON-STEM", " non stem", "RP RP STEM"), so group them on a
// cleaned key: upper case, single spaces, no word repeated twice in a row
const areaKey = (area) =>
  (area ?? "")
    .trim()
    .toUpperCase()
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b(\w+)( \1\b)+/g, "$1") || UNSPECIFIED;

const groupByArea = (counts) => {
  const rows = new Map();
  counts.forEach(({ gender, area, total }) => {
    const key = areaKey(area);
    const row = rows.get(key) ?? { area: key, female: 0, male: 0, other: 0, total: 0 };
    const g = (gender ?? "").toUpperCase();
    if (g === "FEMALE") row.female += total;
    else if (g === "MALE") row.male += total;
    else row.other += total;
    row.total += total;
    rows.set(key, row);
  });
  return [...rows.values()].sort((a, b) => b.total - a.total);
};

const PARTS = [
  { field: "female", label: "Female", color: SERIES.orange },
  { field: "male", label: "Male", color: SERIES.blue },
  { field: "other", label: "Not recorded", color: NEUTRAL },
];

// One 100% bar: each gender's share, split by a surface-coloured gap
function SplitBar({ row, className = "h-3" }) {
  return (
    <div className={`flex gap-[2px] overflow-hidden rounded ${className}`}>
      {PARTS.filter((p) => row[p.field] > 0).map((p) => (
        <span
          key={p.field}
          title={`${p.label}: ${fmt(row[p.field])} (${formatShare(row[p.field], row.total)})`}
          className="h-full"
          style={{ width: `${(row[p.field] / row.total) * 100}%`, backgroundColor: p.color }}
        />
      ))}
    </div>
  );
}

// Students per study area split by gender: the overall split, then one bar per study area
export default function GenderSplit({ counts = [], emptyText = "Nothing recorded for this year" }) {
  const rows = groupByArea(counts);
  if (!rows.length) return <p className="text-sm text-slate-500">{emptyText}</p>;

  const all = rows.reduce(
    (sum, r) => ({ female: sum.female + r.female, male: sum.male + r.male, other: sum.other + r.other, total: sum.total + r.total }),
    { female: 0, male: 0, other: 0, total: 0 }
  );
  const parts = PARTS.filter((p) => p.field !== "other" || all.other > 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {parts.map((p) => (
            <div key={p.field} className="flex items-center gap-2 text-sm">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="text-slate-600">{p.label}</span>
              <span className="font-semibold text-slate-900">{fmt(all[p.field])}</span>
              <span className="text-xs text-slate-400">{formatShare(all[p.field], all.total)}</span>
            </div>
          ))}
        </div>
        <SplitBar row={all} className="h-4" />
      </div>

      <ul className="flex flex-col gap-3 border-t border-slate-100 pt-4">
        {rows.map((r) => (
          <li key={r.area} className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate text-slate-700">{r.area === UNSPECIFIED ? "Study area not recorded" : r.area}</span>
              <span className="shrink-0 text-xs text-slate-500">
                {fmt(r.female)} F · {fmt(r.male)} M · <span className="font-semibold text-slate-900">{fmt(r.total)}</span>
              </span>
            </div>
            <SplitBar row={r} className="h-2" />
          </li>
        ))}
      </ul>
    </div>
  );
}
